import { useState, useEffect, useCallback } from 'react';
import cookieService from '../services/cookieService';

/**
 * Custom hook for managing cookie consent
 * @returns {Object} Cookie consent state and methods
 */
const useCookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [preferences, setPreferences] = useState({
    essential: true,
    analytics: false,
  });

  /**
   * Initialize cookie consent check
   */
  const initialize = useCallback(async () => {
    try {
      setIsLoading(true);
      const hasConsent = await cookieService.hasConsentBeenGiven();

      if (!hasConsent) {
        setShowBanner(true);
      } else {
        // Load existing preferences
        const existingPreferences = await cookieService.getCookiePreferences();
        setPreferences(existingPreferences);
      }
    } catch (error) {
      console.error('Error initializing cookie consent:', error);
      // Show banner on error to be safe
      setShowBanner(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Accept all cookies
   */
  const acceptAll = useCallback(async () => {
    try {
      await cookieService.acceptAllCookies();
      setShowBanner(false);
      setShowDetails(false);
      setPreferences({
        essential: true,
        analytics: true,
      });
    } catch (error) {
      console.error('Error accepting all cookies:', error);
    }
  }, []);

  /**
   * Reject all non-essential cookies
   */
  const rejectAll = useCallback(async () => {
    try {
      await cookieService.rejectAllCookies();
      setShowBanner(false);
      setShowDetails(false);
      setPreferences({
        essential: true,
        analytics: false,
      });
    } catch (error) {
      console.error('Error rejecting all cookies:', error);
    }
  }, []);

  /**
   * Accept selected cookies
   */
  const acceptSelected = useCallback(async (selectedPreferences) => {
    try {
      await cookieService.saveCookiePreferences(selectedPreferences);
      setShowBanner(false);
      setShowDetails(false);
      setPreferences(selectedPreferences);
    } catch (error) {
      console.error('Error accepting selected cookies:', error);
    }
  }, []);

  /**
   * Show cookie details modal
   */
  const showCookieDetails = useCallback(() => {
    setShowDetails(true);
  }, []);

  /**
   * Close cookie details modal
   */
  const closeCookieDetails = useCallback(() => {
    setShowDetails(false);
  }, []);

  /**
   * Check if a specific cookie category is allowed
   */
  const isCategoryAllowed = useCallback(async (category) => {
    try {
      return await cookieService.isCookieCategoryAllowed(category);
    } catch (error) {
      console.error('Error checking cookie category:', error);
      return category === 'essential'; // Only essential cookies allowed by default
    }
  }, []);

  /**
   * Get current cookie preferences
   */
  const getCurrentPreferences = useCallback(async () => {
    try {
      return await cookieService.getCookiePreferences();
    } catch (error) {
      console.error('Error getting cookie preferences:', error);
      return preferences;
    }
  }, [preferences]);

  /**
   * Update cookie preferences
   */
  const updatePreferences = useCallback(async (newPreferences) => {
    try {
      await cookieService.saveCookiePreferences(newPreferences);
      setPreferences(newPreferences);
    } catch (error) {
      console.error('Error updating cookie preferences:', error);
    }
  }, []);

  /**
   * Clear all cookie preferences (for testing or reset)
   */
  const clearPreferences = useCallback(async () => {
    try {
      await cookieService.clearCookiePreferences();
      setShowBanner(true);
      setShowDetails(false);
      setPreferences({
        essential: true,
        analytics: false,
      });
    } catch (error) {
      console.error('Error clearing cookie preferences:', error);
    }
  }, []);

  /**
   * Initialize on mount
   */
  useEffect(() => {
    initialize();
  }, [initialize]);

  return {
    // State
    showBanner,
    showDetails,
    isLoading,
    preferences,

    // Actions
    acceptAll,
    rejectAll,
    acceptSelected,
    showCookieDetails,
    closeCookieDetails,
    isCategoryAllowed,
    getCurrentPreferences,
    updatePreferences,
    clearPreferences,
    initialize,
  };
};

export default useCookieConsent;
